import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/authContextValue';
import { LanguageProvider } from './contexts/LanguageContext';
import Layout from './components/layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import ContentManagement from './pages/ContentManagement';
import { AFFIRMATIONEN_BEREICH, BLOG_BEREICH } from './pages/contentBereiche';
import BunnyCDNManagement from './pages/BunnyCDNManagement';
import TopicCategoryManagement from './pages/TopicCategoryManagement';
import AudioManagement from './pages/AudioManagement';
import VideoManagement from './pages/VideoManagement';
import PlaylistManagement from './pages/PlaylistManagement';
import AlbumManagement from './pages/AlbumManagement';
import ArtistManagement from './pages/ArtistManagement';
import MindBookManagement from './pages/MindBookManagement';
import UserManagement from './pages/UserManagement';

// Roles allowed into the CMS (Phase 2 WP6 / E4). Authoritative role comes from the
// backend User record (DB), surfaced via AuthContext.
const CMS_ROLES = ['ADMIN', 'CONTENT_CREATOR', 'MODERATOR'];

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user, logout } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Authenticated but lacking a CMS role (e.g. FREE/PREMIUM): deny here rather than
  // redirect to /login (PublicRoute would bounce an authenticated user back → loop).
  if (!user || !CMS_ROLES.includes(user.role)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-center p-6">
        <h1 className="text-2xl font-bold">Kein Zugriff</h1>
        <p className="text-gray-500">
          Ihr Konto{user?.role ? ` (${user.role})` : ''} hat keine Berechtigung für das CMS.
        </p>
        <button
          onClick={() => logout()}
          className="px-4 py-2 rounded bg-gray-800 text-white"
        >
          Abmelden
        </button>
      </div>
    );
  }

  return <>{children}</>;
};

// Public Route Component (redirect if authenticated)
// Exportiert, damit die Kopplung „Ladezustand haengt die Seite aus" testbar ist. Genau die
// hat die Fehlermeldung der Anmeldung verschluckt — ein Test nur fuer Login.tsx waere vorher
// wie nachher gruen gewesen, weil das Aushaengen HIER passiert, nicht dort.
export const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/" replace /> : <>{children}</>;
};

// Main App Routes
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      {/* ⚠️ Hier stand die Route `/register`. Sie legte Konten mit der Rolle FREE an — und der
          Zugriffsschutz des CMS laesst nur ADMIN, MODERATOR und CONTENT_CREATOR hinein
          (`CMS_ROLLEN` weiter unten). Wer sich registrierte, bekam ein Konto, mit dem er sich
          nicht anmelden konnte: eine Tuer, die aufgeht und in eine Wand fuehrt.
          Entfernt statt repariert (Tom 2026-08-13), wie Analytics und Einstellungen — wie
          Mitarbeiter angelegt werden, ist eine eigene Entscheidung und keine Nebenwirkung einer
          oeffentlichen Registrierung. */}

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="profile" element={<Profile />} />
        {/* ⚠️ Beide Routen hingen bis 2026-08-12 an `<ContentManagement />` OHNE Vorgabe — zwei
            Menüpunkte, dieselbe ungefilterte Liste, identischer Inhalt. Jetzt eine Komponente mit
            zwei Bereichs-Vorgaben; der Typ wandert serverseitig in die Abfrage.
            Warum gefiltert und nicht (wie Analytics/Einstellungen) gestrichen: Affirmationen sind
            ein echter Prisma-Typ mit eigenem Backend-Endpunkt und einem laufenden Verbraucher in
            der öffentlichen Web-App — Begründung im Kopf von `pages/contentBereiche.ts`. */}
        <Route path="content/blog" element={<ContentManagement bereich={BLOG_BEREICH} />} />
        <Route path="content/affirmations" element={<ContentManagement bereich={AFFIRMATIONEN_BEREICH} />} />
        <Route path="audio" element={<AudioManagement />} />
        <Route path="video" element={<VideoManagement />} />
        <Route path="playlists" element={<PlaylistManagement />} />
        <Route path="albums" element={<AlbumManagement />} />
        <Route path="artists" element={<ArtistManagement />} />
        <Route path="mindbooks" element={<MindBookManagement />} />
        <Route path="bunny-cdn" element={<BunnyCDNManagement />} />
        <Route path="topic-categories" element={<TopicCategoryManagement />} />
        <Route path="users" element={<UserManagement />} />
        {/* ⚠️ `analytics` und `settings` standen hier bis 2026-08-12 als Route auf ein leeres
            `<div>` — im Menü sichtbar, dahinter nichts, und anders als bei „Benutzer" fehlt auch
            das Backend (26× „to be implemented"). Beide sind samt Menüeintrag entfernt: eine
            sichtbare Funktion soll entweder wirklich funktionieren oder gar nicht erscheinen.
            Alte Lesezeichen fängt der Catch-all unten ab. */}
      </Route>

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <LanguageProvider>
      <AuthProvider>
        <Router>
          <div className="app-container">
            <AppRoutes />
          </div>
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
};

export default App;